/**
 * Per-user email notification preferences.
 *
 * Server-side state, not `state/user-preferences.ts`: this drives mail
 * the backend sends when no browser is open, so it cannot live in one
 * device's localStorage. The key is deliberately not site-scoped — the
 * row belongs to the person, and which sites it covers is decided at
 * fan-out time from their site assignments.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchNotificationPrefs, updateNotificationPrefs } from '../v1-client';
import type { UpdateUserNotificationPrefs, UserNotificationPrefs } from '../v1';
import { getRuntimeConfig } from '../../config/runtime-config';
import { getRequestBearerToken } from '../../auth/request-auth';
import { usePreviewRole } from '../../auth/useRolePreview';

export const NOTIFICATION_PREFS_KEY = 'notification-prefs' as const;
export const notificationPrefsKey = () => [NOTIFICATION_PREFS_KEY] as const;

export const NOTIFICATION_DELIVERY_KEY = 'notification-delivery' as const;
export const notificationDeliveryKey = () => [NOTIFICATION_DELIVERY_KEY] as const;

/**
 * Whether the server could actually email THIS user if they asked it
 * to. Three independent preconditions, none of which the preference
 * row knows about:
 *
 * - `providerConfigured` — the deployment has mail credentials at all.
 *   Absent ones are not an error anywhere: the alert still opens, the
 *   send is recorded as a failed delivery, and nothing surfaces.
 * - `addressKnown` — we learned an address for this principal when
 *   they signed in.
 * - `notified_site_count` — how many sites the fan-out would actually
 *   match: their explicit selection when they have one, else their
 *   assignments. Zero means subscribed to nothing.
 *
 * Read by the profile screen so the toggle stops promising pages the
 * deployment cannot send. Snake_case because it is the api-local shape
 * `api/users.py` returns (like `AlertOut`), not a generated v1
 * contract type.
 */
export interface EmailDeliverability {
  deliverable: boolean;
  provider: string;
  provider_configured: boolean;
  provider_detail: string | null;
  address_known: boolean;
  assigned_site_count: number;
  notified_site_count: number;
  /**
   * This principal reaches every site by role rather than by
   * assignment. It changes what "you will receive nothing" means and
   * so what to tell them: an operator has to ask an administrator for
   * a site, an admin only has to pick one.
   */
  all_sites_access: boolean;
}

// Fetched here rather than through `v1-client.ts`, which carries the
// generated v1 contract surface and whose `apiFetch` is module-private
// — same reason `hooks/index.ts` calls `fetch` directly for alerts.
async function fetchEmailDeliverability(): Promise<EmailDeliverability> {
  const token = getRequestBearerToken();
  const res = await fetch(
    `${getRuntimeConfig().apiBaseUrl}/api/v1/users/me/notification-delivery`,
    {
      cache: 'no-store',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as EmailDeliverability;
}

/**
 * Deployment-level state, so it is polled slowly rather than kept
 * fresh: it changes when an administrator adds a Secret or a site
 * assignment, not while somebody is looking at the form.
 */
export function useEmailDeliverability() {
  // `all_sites_access` is the server's answer for the REAL token, and
  // only platform_admin holds it (`guards.py::has_all_sites_access`).
  // No role a preview can show holds it, so during "View as role" it
  // reads false -- the copy the previewed role would actually get.
  const previewRole = usePreviewRole();
  return useQuery<EmailDeliverability, Error, EmailDeliverability>({
    queryKey: notificationDeliveryKey(),
    queryFn: fetchEmailDeliverability,
    staleTime: 5 * 60_000,
    ...(previewRole ? { select: (d: EmailDeliverability) => ({ ...d, all_sites_access: false }) } : {}),
  });
}

export function useNotificationPrefs() {
  return useQuery<UserNotificationPrefs>({
    queryKey: notificationPrefsKey(),
    queryFn: fetchNotificationPrefs,
  });
}

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation<UserNotificationPrefs, Error, UpdateUserNotificationPrefs>({
    mutationFn: (body) => updateNotificationPrefs(body),
    // The PUT answers with the merged row, so seed the cache from it
    // rather than refetching: the toggle and the severity select are
    // on the same form, and an invalidate-then-refetch round trip
    // would briefly render the pre-change value under the control the
    // operator just moved.
    onSuccess: (prefs) => qc.setQueryData(notificationPrefsKey(), prefs),
  });
}
